import { ensureRouteData } from '@/app/query-client'
import { marketEventsTableQueryOptions } from '@/features/market-events/api/queries'
import { todayAdIso } from '@/lib/bsCalendarMath'
import { eventsQuerySchema, type EventsQuery } from '@/schemas/market-events'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The one parse `EventsScreen` reads `search` through.
 *
 * `Route.useSearch()` merges the URL's raw query string into the validated object — an
 * unrecognised param (`?utm_source=...`) rides along even though `eventsQuerySchema` never named it,
 * and the query key is the whole object, so the screen's key would differ from the one the loader
 * warmed for the same URL: a second request and a flash for a link nobody meant to change.
 */
export const toEventsQuery = (search: unknown): EventsQuery => eventsQuerySchema.parse(search)

/**
 * What the calendar asks mm-be for. The date chip opens on today, as the mock's does, so a bare
 * `/market/events` is "today onward" — `start` is sent even though the URL names no day, and the URL
 * itself stays clean (a picked day or range is what lands in it).
 *
 * One function for the loader and the screen so the two can never key the same URL differently.
 */
export const toEventsListQuery = (search: EventsQuery): EventsQuery => ({
  ...search,
  date: search.date ?? todayAdIso(),
})

/**
 * The `/market/events` loader.
 *
 * MAIN: the entry URL's page of the calendar, awaited — one request now that mm-be searches,
 * filters by type and cuts by date itself, where the whole ~977-event set used to be five.
 *
 * Read from `location.search` through the route's own schema, not `loaderDeps`: a dep would make
 * every filter or pager click a new route match — the full-page skeleton on each one. As it is, a
 * search-only change runs no loader at all; `EventsScreen` re-keys `useMarketEvents` for itself and
 * keeps the previous page up while the next one loads (`keepPreviousData`).
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root of its own: typed inside
 * `createFileRoute`, a loader is bound to this route's parent in the generated tree.
 */
export async function loadEvents({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  await ensureRouteData(
    queryClient,
    marketEventsTableQueryOptions(toEventsListQuery(toEventsQuery(location.search))),
  )
}
